'use client';

import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["slots", "slotProps", "className", "classes"];
import * as React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { SuggestionsRoot } from '@mui/x-chat-headless';
import { styled, createUseThemeProps } from "../internals/zero-styled/index.mjs";
import { useChatSuggestionsUtilityClasses } from "./chatSuggestionsClasses.mjs";
import { jsx as _jsx } from "react/jsx-runtime";
const useThemeProps = createUseThemeProps('MuiChatSuggestions');
const ChatSuggestionsRootStyled = styled('div', {
  name: 'MuiChatSuggestions',
  slot: 'Root',
  overridesResolver: (_, styles) => styles.root
})(({
  theme
}) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  justifyContent: 'center',
  padding: theme.spacing(2),
  pointerEvents: 'auto'
}));
const ChatSuggestionItemStyled = styled('button', {
  name: 'MuiChatSuggestions',
  slot: 'Item',
  overridesResolver: (_, styles) => styles.item
})(({
  theme
}) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: theme.spacing(0.75, 1.5),
  borderRadius: theme.shape.borderRadius * 4,
  border: `1px solid ${(theme.vars || theme).palette.divider}`,
  backgroundColor: (theme.vars || theme).palette.background.paper,
  color: (theme.vars || theme).palette.text.primary,
  fontSize: theme.typography.body2.fontSize,
  fontFamily: theme.typography.fontFamily,
  cursor: 'pointer',
  transition: theme.transitions.create(['background-color', 'border-color']),
  '&:hover': {
    backgroundColor: (theme.vars || theme).palette.action.hover,
    borderColor: (theme.vars || theme).palette.primary.main
  },
  '&:focus-visible': {
    outline: `2px solid ${(theme.vars || theme).palette.primary.main}`,
    outlineOffset: 2
  }
}));
const ChatSuggestions = /*#__PURE__*/React.forwardRef(function ChatSuggestions(inProps, ref) {
  const props = useThemeProps({
    props: inProps,
    name: 'MuiChatSuggestions'
  });
  const {
      slots,
      slotProps,
      className,
      classes: classesProp
    } = props,
    other = _objectWithoutPropertiesLoose(props, _excluded);
  const classes = useChatSuggestionsUtilityClasses(classesProp);
  return /*#__PURE__*/_jsx(SuggestionsRoot, _extends({
    ref: ref
  }, other, {
    slots: _extends({}, slots, {
      root: slots?.root ?? ChatSuggestionsRootStyled,
      item: slots?.item ?? ChatSuggestionItemStyled
    }),
    slotProps: _extends({}, slotProps, {
      root: _extends({
        className: clsx(classes.root, className)
      }, slotProps?.root),
      item: _extends({
        className: classes.item
      }, slotProps?.item)
    })
  }));
});
if (process.env.NODE_ENV !== "production") ChatSuggestions.displayName = "ChatSuggestions";
process.env.NODE_ENV !== "production" ? ChatSuggestions.propTypes = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // | To update them edit the TypeScript types and run "pnpm proptypes"  |
  // ----------------------------------------------------------------------
  /**
   * By default, suggestions only render when the active thread has no messages
   * (treating them as an empty-state affordance). Set to `true` to render the
   * suggestions regardless of message count — e.g. as a "next prompt" row above
   * the composer in an active conversation.
   * @default false
   */
  alwaysVisible: PropTypes.bool,
  /**
   * Whether to auto-submit when a suggestion is clicked.
   * @default false
   */
  autoSubmit: PropTypes.bool,
  children: PropTypes.node,
  classes: PropTypes.object,
  className: PropTypes.string,
  slotProps: PropTypes.object,
  slots: PropTypes.object,
  /**
   * Suggestion items. Strings are normalized to `{ value, label }`.
   * Ignored when `children` are provided.
   */
  suggestions: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string.isRequired
  }), PropTypes.string]).isRequired)
} : void 0;
export { ChatSuggestions };